import { makeStyles } from "tss-react/mui";
import { Grid, Typography, Button, Table, TableBody, TableRow, DialogTitle, DialogContent, DialogActions, TableCell, Alert, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useEntity, useEntities } from "@emberly/zenith-client";
import { useMemo, useState } from "react";
import AgreementProductPicker from "../AgreementProductPicker";
import { useStation } from "../../../../providers/StationProvider";
import { GetVatRate, MakeOrderLineItem } from "../../../../common/orders";
import { OrderEnums, OrderEnumsLists } from "../../../../common/constants";
import TextFieldDecimal from "../../inputs/TextFieldDecimal";
import ResponsiveDialog from "../../core/ResponsiveDialog";
import { useMission } from "../../../../providers/MissionProvider";
import { GetDefaultTaskRefId, GetTaskLabel } from "../../../../common/mission";
import { useParams } from "react-router";

const useStyles = makeStyles()(
  (theme) => ({
    table: {
      width: "100%",
      "& td": {
        border: "none",
        padding: theme.spacing(1),
        paddingLeft: 0,
        paddingBottom: theme.spacing(0.25),
        paddingTop: theme.spacing(0.25),
      }
    }
  })
);

export default function ReferencedOrderLineItemDialog(props) {
  const { edit, onClose, index, orderLine, disableTaskConnection } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { pushToList, updateEntityField, spliceList } = useEntity();
  const { priceUnit } = useStation();

  const { mission } = useMission();
  const taskList = mission.salvageTasks;
  const { taskId, taskNumber } = useParams();

  const [taskRef, setTaskRef] = useState(disableTaskConnection ? null : ((edit && !!orderLine ? orderLine.taskRef?.id : GetDefaultTaskRefId(taskId, taskNumber, taskList)) || null));

  const [searchProductItem, setSearchProductItem] = useState(edit && !!orderLine ? orderLine : null);
  const [price, setPrice] = useState(edit && !!orderLine ? orderLine.price.value : "0");
  const [quantity, setQuantity] = useState(edit && !!orderLine ? orderLine.quantity : "1");
  const [discount, setDiscount] = useState(edit && !!orderLine ? orderLine.discount : "0");
  const [currency, setCurrency] = useState(edit && !!orderLine ? orderLine.price.currency : priceUnit);

  const { entities: vatCodes } = useEntities("VatCode");
  const { entities: productGroups, loading: productGroupsLoading } = useEntities("ProductGroup");

  const missingProductGroup = useMemo(() => !productGroupsLoading && !!searchProductItem?.productGroup?.id && !productGroups.find(t => t.id === searchProductItem?.productGroup?.id), [productGroups, searchProductItem, productGroupsLoading]);

  const onSubmit = () => {
    const item = MakeOrderLineItem(
      searchProductItem,
      { value: price, currency },
      quantity, discount, productGroups, vatCodes,
      !!taskRef ? { id: taskRef, name: `${taskList.find(t => t.taskId === taskRef)?.number || ""}` } : null
    );
    if (edit) {
      updateEntityField(`orderLines.${index}`, item);
    } else {
      pushToList("orderLines", item);
    }
    onClose();
  };

  const onDelete = () => {
    spliceList("orderLines", index);
    onClose();
  };


  const onSearch = (product) => {
    setSearchProductItem(product);

    if (!!product) {
      setCurrency(product.price.currency !== OrderEnums.Currency.None ? product.price.currency : priceUnit);
      setQuantity("1");

      if (product.discounted) {
        setPrice(product.discount.value);
      } else {
        setPrice(product.price.value);
      }

    } else {
      setPrice("0");
      setDiscount("0");
      setQuantity("1");
    }
  };

  return (
    <ResponsiveDialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>
        {edit ? t("order:referencedOrderLineItemDialog:editTitle") : t("order:referencedOrderLineItemDialog:createTitle")}
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={1}>

          {!!missingProductGroup ? (
            <Grid item xs={12}>
              <Alert severity="error">
                {t("order:referencedOrderLineItemDialog:missingProductGroup")}
              </Alert>
            </Grid>
          ) : null}

          {
            !disableTaskConnection ? (
              <Grid item xs={12}>
                <FormControl fullWidth size="small" variant="filled">
                  <InputLabel>{t("order:referencedOrderLineItemDialog:taskRef")}</InputLabel>
                  <Select
                    value={taskRef || ""}
                    onChange={(ev) => setTaskRef(ev.target.value)}
                  >

                    <MenuItem value="">{t("order:referencedOrderLineItemDialog:noTaskRef")}</MenuItem>

                    {
                      taskList.map((task, index) => (
                        <MenuItem value={task.taskId} key={index}>{GetTaskLabel(task, t)}</MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </Grid>
            ) : null
          }


          <Grid item xs={12}>
            <AgreementProductPicker
              label={t("order:referencedOrderLineItemDialog:search")}
              onChange={onSearch}
              value={searchProductItem}
              path="name"
            />
          </Grid>

          <Grid item xs={12}>
            <TextFieldDecimal
              label={t("order:referencedOrderLineItemDialog:price")}
              onChange={ev => setPrice(ev.target.value)}
              value={price}
              variant="filled"
              selectOnFocus
              decimal
              fullWidth
              size="small"
              suffix={` ${t(`order:enums:currencyShortHand:${OrderEnumsLists.Currency[currency]}`)}`}
            />
          </Grid>


          <Grid item xs={12}>
            <TextFieldDecimal
              label={t("order:referencedOrderLineItemDialog:quantity")}
              onChange={ev => setQuantity(ev.target.value)}
              value={quantity}
              variant="filled"
              selectOnFocus
              decimal
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={12}>
            <TextFieldDecimal
              label={t("order:referencedOrderLineItemDialog:discount")}
              onChange={ev => setDiscount(ev.target.value)}
              value={discount}
              variant="filled"
              selectOnFocus
              decimal
              fullWidth
              size="small"
              suffix=" %"
            />
          </Grid>

          <Grid item xs={12}>
            <Table className={classes.table}>
              <TableBody>

                <TableRow>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">{t("order:referencedOrderLineItemDialog:vat")}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{!!searchProductItem?.productGroup ? `${GetVatRate(searchProductItem.productGroup.id, productGroups, vatCodes)}%` : "-"}</Typography>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">{t("order:referencedOrderLineItemDialog:code")}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{searchProductItem?.code || "-"}</Typography>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">{t("order:referencedOrderLineItemDialog:description")}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{searchProductItem?.description || "-"}</Typography>
                  </TableCell>
                </TableRow>

              </TableBody>
            </Table>
          </Grid>

        </Grid>
      </DialogContent>
      <DialogActions>
        {!!edit ? (
          <Button onClick={onDelete}>
            {t("common:remove")}
          </Button>
        ) : null}
        <Button onClick={onClose} variant={!!edit ? "outlined" : undefined}>
          {t("common:cancel")}
        </Button>
        <Button variant="contained" color="primary" onClick={onSubmit} disabled={!searchProductItem || missingProductGroup}>
          {edit ? t("common:change") : t("common:add")}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}


